import { useState, useRef, useEffect } from 'react'
import { useAuth } from './AuthContext.tsx'
import { api } from './api.ts'
import { TermsAndPrivacyModal } from './TermsAndPrivacyModal.tsx'
import { Modal } from './Modal.tsx'

interface AuthModalProps {
  onClose: () => void
  onSignInSuccess?: () => void
}

export function AuthModal({ onClose, onSignInSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<'signin' | 'signup' | 'forgot'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [termsModalTab, setTermsModalTab] = useState<'terms' | 'privacy'>('terms')
  const { signIn, signUp } = useAuth()
  const emailInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (mode === 'signin' && emailInputRef.current) {
      emailInputRef.current.focus()
    }
  }, [mode])

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      await signIn(email, password)
      onClose()
      onSignInSuccess?.()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      await signUp(email, password, name)
      onClose()
      onSignInSuccess?.()
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')
    try {
      await api.auth.forgotPassword(email)
      setMessage('Password reset link sent to your email')
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      const url = await api.auth.getGoogleAuthUrl()
      window.location.href = url
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title={mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Forgot Password'}
        width="max-w-md w-full"
        className="bg-[#2a2a2a]"
        contentClassName="!p-8"
        headerClassName="!mb-6"
        usePortal={true}
      >

        {error && <div className="bg-red-500 text-white p-3 rounded mb-4">{error}</div>}
        {message && <div className="bg-green-500 text-white p-3 rounded mb-4">{message}</div>}

        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="space-y-4">
            <input
              ref={emailInputRef}
              type="email"
              placeholder="Email"
              value={email}
              autoComplete="email"
              onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-[#333] border border-[#444] rounded text-white"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              autoComplete="current-password"
              onChange={e => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-[#333] border border-[#444] rounded text-white"
              required
            />
            <button
              type="submit"
              className="w-full px-4 py-2 bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white rounded font-semibold hover:opacity-90"
            >
              Sign In
            </button>
            {/* <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full px-4 py-2 bg-white text-black rounded font-semibold hover:bg-gray-100"
            >
              Sign in with Google
            </button> */}
            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={() => setMode('forgot')}
                className="text-[#667eea] hover:underline text-sm"
              >
                Forgot password?
              </button>
              <div>
                <span className="text-[#888]">Don't have an account? </span>
                <button
                  type="button"
                  onClick={() => setMode('signup')}
                  className="text-[#667eea] hover:underline"
                >
                  Sign up
                </button>
              </div>
            </div>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="space-y-4">
            <input
              type="text"
              placeholder="Artist Name"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full px-4 py-2 bg-[#333] border border-[#444] rounded text-white"
              required
            />
            <input
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-[#333] border border-[#444] rounded text-white"
              required
            />
            <input
              type="password"
              placeholder="Password"
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-[#333] border border-[#444] rounded text-white"
              required
            />
            <button
              type="submit"
              className="w-full px-4 py-2 bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white rounded font-semibold hover:opacity-90"
            >
              Sign Up
            </button>
            {/* <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full px-4 py-2 bg-white text-black rounded font-semibold hover:bg-gray-100"
            >
              Sign up with Google
            </button> */}
            <div className="text-xs text-[#888] text-center">
              By signing up, you agree to our{' '}
              <button
                type="button"
                onClick={e => {
                  e.preventDefault()
                  setTermsModalTab('terms')
                  setShowTermsModal(true)
                }}
                className="text-[#667eea] hover:underline"
              >
                Terms and Conditions
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={e => {
                  e.preventDefault()
                  setTermsModalTab('privacy')
                  setShowTermsModal(true)
                }}
                className="text-[#667eea] hover:underline"
              >
                Privacy Statement
              </button>
            </div>
            <div className="text-center">
              <span className="text-[#888]">Already have an account? </span>
              <button
                type="button"
                onClick={() => setMode('signin')}
                className="text-[#667eea] hover:underline"
              >
                Sign in
              </button>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-[#333] border border-[#444] rounded text-white"
              required
            />
            <button
              type="submit"
              className="w-full px-4 py-2 bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white rounded font-semibold hover:opacity-90"
            >
              Send Reset Link
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setMode('signin')}
                className="text-[#667eea] hover:underline"
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </Modal>
      {showTermsModal && (
        <TermsAndPrivacyModal onClose={() => setShowTermsModal(false)} initialTab={termsModalTab} />
      )}
    </>
  )
}
